import { Injectable, inject } from '@angular/core';
import { Firestore, collection, collectionData, doc, getDoc, getDocs, onSnapshot, setDoc, updateDoc, where, QuerySnapshot, DocumentData, onSnapshotsInSync, orderBy, query, snapToData, deleteDoc, Query, DocumentSnapshot, DocumentReference, arrayUnion } from '@angular/fire/firestore';
import { Store } from '@ngrx/store';
import { BaseState } from '../reducers';
import { Observable, combineLatest, delay, filter, firstValueFrom, forkJoin, from, map, shareReplay, switchMap, take, takeUntil, withLatestFrom } from 'rxjs';
import { selectGroupId, selectUserId, selectUserState } from '../users/store/user.selector';
import { Comment, FirebaseRenderData, FirebaseUsageStats, GroupFolder, GroupRenderData } from './firebase.model';
import { getDownloadURL, getStorage, ref, uploadBytes } from '@angular/fire/storage';
import { FirebaseApp } from '@angular/fire/app';
import { Preset, PresetCreationRequest } from '../editor/models/preset.model';
import { generateUUID } from '../editor/utils/store.utils';
import { selectRenderId } from '../editor/store/editor.selector';
import { DocsRendererComponent } from '../docs/docs-renderer/docs-renderer.component';
import { UserData } from '../users/store/user.reducer';

@Injectable({
  providedIn: 'root'
})
export class FirebaseFirestoreService {
  private userId$ = this.store.select(selectUserId)
  private userState$ = this.store.select(selectUserState)
  private groupId$ = this.userState$.pipe(map(x => x?.groupId))
  private firestore = inject(Firestore)
  private firebaseApp = inject(FirebaseApp)

  private renderListStream$: Observable<QuerySnapshot<DocumentData, DocumentData>>

  private userState: UserData | undefined

  constructor(private store: Store<BaseState>) {
    this.userState$.subscribe(x => this.userState = x)
  }

  public getUserData(uid: string) {
    return getDoc(doc(this.firestore, `users/${uid}`));
  }

  private collectionSnapshot$<T>(query: Query) {
    return new Observable<T[]>(subscriber => {
      const unsubscribe = onSnapshot(query, snapshot => {
        subscriber.next(snapshot.docs.map(doc => doc.data() as T));
      }, error => {
          subscriber.error(error);
        });
      return unsubscribe;
    });
  }

  documentSnapshot$<T = DocumentData>(
    docRef: DocumentReference<DocumentData>
  ) {
    return new Observable<T | undefined>((subscriber) => {
      const unsubscribe = onSnapshot(
        docRef,
        (snapshot: DocumentSnapshot<DocumentData>) => {
          if (snapshot.exists()) {
            subscriber.next({ id: snapshot.id, ...snapshot.data() } as T);
          } else {
            subscriber.next(undefined);
          }
        },
        (error) => {
          subscriber.error(error);
        }
      );
      return unsubscribe;
    });
  }

  public async getGroupData() {
    const groupId = await firstValueFrom(this.groupId$.pipe(
      filter(x => !!x)
    ))
    console.log(groupId)
    return this.documentSnapshot$(doc(this.firestore, `groups/${groupId}`))
  }


  public async copyFirebaseRenderExamples() {
    const source = 'users/xQz1KVibOxabWAIFfgVlmUh96SH3/renders'; // Change to your source collection
    const destination = 'groups/testing/renders'; // Change to your destination

    const sourceCol = collection(this.firestore, source);
    const snapshot = await getDocs(sourceCol);

    for (const docSnap of snapshot.docs) {
      await setDoc(
        doc(this.firestore, destination, docSnap.id),
        {
          ...docSnap.data(),
          parentFolder: 'root'
        }
      );
    }
  }

  public getGroupFolders() {
    return this.store.select(selectUserState).pipe(
      map(x => x?.groupId),
      filter(x => !!x),
      switchMap(groupId => {
        if (!groupId) {
          throw "User does not belong to enterprise group"
        }
        const foldersRef = collection(this.firestore, `groups/${groupId}/folders`)
        const folderQuery = query(foldersRef, orderBy('name', 'desc'))
        return combineLatest({
          folders: this.collectionSnapshot$<GroupFolder>(folderQuery),
        })
      })
    )
  }

  public getGroupRenders(folderId: string) {
    return this.store.select(selectUserState).pipe(
      map(x => x?.groupId),
      filter(x => !!x),
      switchMap(groupId => {
        if (!groupId) {
          throw "User does not belong to enterprise group"
        }
        const rendersRef = collection(this.firestore, `groups/${groupId}/renders`)
        const renderQuery = query(
          rendersRef,
          where('parentFolder', '==', folderId),
          orderBy('createdTimestamp', 'desc')
        )
        return this.collectionSnapshot$<GroupRenderData>(renderQuery)
      }),
      map(renders => renders ?? [])
    )
  }

  public getGroupRendersAndFolders() {
    return this.store.select(selectUserState).pipe(
      map(x => x?.groupId),
      filter(x => !!x),
      switchMap(groupId => {
        if (!groupId) {
          throw "User does not belong to enterprise group"
        }
        const foldersRef = collection(this.firestore, `groups/${groupId}/folders`)
        const folderQuery = query(foldersRef)

        const rendersRef = collection(this.firestore, `groups/${groupId}/renders`)
        const renderQuery = query(rendersRef)

        return combineLatest({
          folders: this.collectionSnapshot$<GroupFolder>(folderQuery),
          renders: this.collectionSnapshot$<GroupRenderData>(renderQuery)
        })
      }),
    )
  }

  public newGroupFolder(parentFolder: string, name: string) {
    const id = generateUUID()
    return firstValueFrom(this.store.select(selectUserState).pipe(
      map(x => x?.groupId),
      filter(x => !!x),
      switchMap(groupId => {
        if (!groupId) {
          throw "User does not belong to enterprise group"
        }
        const folder: GroupFolder = {
          id,
          name,
          parentFolder
        }
        return setDoc(doc(this.firestore, `groups/${groupId}/folders/${id}`), folder)
      })),
    )
  }

  public renameFolderGroup(folder: GroupFolder, name: string) {
    return firstValueFrom(this.store.select(selectUserState).pipe(
      map(x => x?.groupId),
      filter(x => !!x),
      switchMap(groupId => {
        if (!groupId) {
          throw "User does not belong to enterprise group"
        }
        folder.name = name
        return setDoc(doc(this.firestore, `groups/${groupId}/folders/${folder.id}`), folder)
      })),
    )
  }

  public moveGroupRender(render: GroupRenderData) {
    return firstValueFrom(this.store.select(selectUserState).pipe(
      map(x => x?.groupId),
      filter(x => !!x),
      switchMap(groupId => {
        if (!groupId) {
          throw "User does not belong to enterprise group"
        }
        return setDoc(doc(this.firestore, `groups/${groupId}/renders/${render.id}`), render)
      })),
    )
  }

  public deleteGroupRender(render: GroupRenderData) {
    return firstValueFrom(this.store.select(selectUserState).pipe(
      map(x => x?.groupId),
      filter(x => !!x),
      switchMap(groupId => {
        if (!groupId) {
          throw "User does not belong to enterprise group"
        }
        return deleteDoc(doc(this.firestore, `groups/${groupId}/renders/${render.id}`))
      })),
    )
  }

  public cloneGroupRender(renderData: GroupRenderData) {
    const id = generateUUID()
    return firstValueFrom(this.store.select(selectUserState).pipe(
      map(x => x?.groupId),
      filter(x => !!x),
      map((groupId) => {
        renderData.id = id
        renderData.lastUpdated = new Date().toISOString()
        renderData.created = new Date().toISOString()
        renderData.createdTimestamp = Math.floor(new Date().getTime())
        renderData.renderName = `Copy of ${renderData.renderName}`
        return setDoc(doc(this.firestore, `groups/${groupId}/renders/${id}`), renderData)
      })
    ))
  }

  public getUserPresets() {
    return this.userId$.pipe(
      filter(userId => !!userId),
      take(1),
      map(userId => {
        return new Observable<QuerySnapshot<DocumentData, DocumentData>>((observer) => {
          const ref = collection(this.firestore, `users/${userId}/presets`)
          const search = query(ref, orderBy('createdTimestamp', 'desc'))
          onSnapshot(search, (snapshot) => {
            observer.next(snapshot)
          })
        })
      }),
      switchMap(x => x)
    )
  }

  public getGlobalPresets() {
    const ref = collection(this.firestore, 'globalPresets')
    const search = query(ref, orderBy('createdTimestamp', 'desc'))
    return from(getDocs(search))
  }

  public createPersonalPreset(preset: PresetCreationRequest) {
    const id = generateUUID()
    const data: Preset = {
      id,
      createdTimestamp: Date.now(),
      ...preset
    }
    return firstValueFrom(this.userId$.pipe(
      filter(userId => !!userId),
      take(1),
      map(userId => setDoc(doc(this.firestore, `users/${userId}/presets/${id}`), data))
    ))
  }

  public cloneRender(baseRenderId: string) {
    const id = generateUUID()
    return firstValueFrom(this.userId$.pipe(
      filter(userId => !!userId),
      take(1),
      switchMap(userId => {
        if (!userId) {
          return []
        }
        return from(this.getRenderData(userId, baseRenderId))
      }),
      withLatestFrom(this.userId$),
      map(([renderData,userId]) => {
        renderData.id = id
        renderData.lastUpdated = new Date().toISOString()
        renderData.created = new Date().toISOString()
        renderData.createdTimestamp = Math.floor(new Date().getTime())
        return setDoc(doc(this.firestore, `users/${userId}/renders/${id}`), renderData)
      })
    ))
  }


  public makePresetGlobal(preset: PresetCreationRequest) {
    const id = generateUUID()
    const data: Preset = {
      id,
      createdTimestamp: Date.now(),
      ...preset,
    }
    setDoc(doc(this.firestore, `globalPresets/${id}`), data)
  }

  public submitPersonalPreset(preset: PresetCreationRequest) {
    const id = generateUUID()
    const data: Preset = {
      id,
      createdTimestamp: Date.now(),
      ...preset
    }
    setDoc(doc(this.firestore, `presetSubmissions/${id}`), data)
  }

  public deletePersonalPreset(id: string) {
    return firstValueFrom(this.userId$.pipe(
      filter(userId => !!userId),
      take(1),
      map(userId => deleteDoc(doc(this.firestore, `users/${userId}/presets/${id}`)))
    ))
  }

  public getRenderListPipe() {
    if (!this.renderListStream$) {
      this.renderListStream$ = this.userId$.pipe(
        filter(userId => !!userId),
        take(1),
        map(userId => {
          return new Observable<QuerySnapshot<DocumentData, DocumentData>>((observer) => {
            const ref = collection(this.firestore, `users/${userId}/renders`)
            const search = query(ref, orderBy('createdTimestamp', 'desc'))
            onSnapshot(search, (snapshot) => {
              observer.next(snapshot)
            })
          })
        }),
        switchMap(x => x)
      )
    }
    return this.renderListStream$
  }

  public uploadGroupThumbnailImageAndGetAddress(image: Blob, groupId: string, id: string) {
    return firstValueFrom(this.userId$.pipe(
      filter(userId => !!userId),
      take(1),
      map(async userId => {
        const storage = getStorage(this.firebaseApp, "gs://thumbnail-render");
        const doc = ref(storage, `${userId}/${id}`)
        const result = await uploadBytes(doc, image, { contentType: 'image/jpeg' })
        return await getDownloadURL(result.ref)
      })
    ))
  }

  public uploadThumbnailImageAndGetAddress(image: Blob, id: string) {
    return firstValueFrom(this.userId$.pipe(
      filter(userId => !!userId),
      take(1),
      map(async userId => {
        const storage = getStorage(this.firebaseApp, "gs://thumbnail-render");
        const doc = ref(storage, `${userId}/${id}`)
        const result = await uploadBytes(doc, image, { contentType: 'image/jpeg' })
        return await getDownloadURL(result.ref)
      })
    ))
  }

  public addGroupComment(renderId: string, text: string) {
    return firstValueFrom(this.groupId$.pipe(
      filter(groupId => !!groupId),
      take(1),
      withLatestFrom(this.userState$),
      switchMap(async ([groupId, userState]) => {
        const docRef = doc(this.firestore, `groups/${groupId}/renders/${renderId}`)
        const comment: Comment = {
          text,
          author: userState?.name || "unnamed",
          authorId: userState?.userId || "x",
          created: new Date().toISOString()
        }
        updateDoc(docRef, {
          comments: arrayUnion(comment)
        })
        return comment
      })
    ))
  }


  public updateGroupRenderData(renderData: Partial<FirebaseRenderData>) {
    return firstValueFrom(this.groupId$.pipe(
      filter(groupId => !!groupId),
      take(1),
      switchMap(async groupId => {
        const docRef = doc(this.firestore, `groups/${groupId}/renders/${renderData.id}`)
        return await updateDoc(docRef, {...renderData, lastUpdated: new Date().toISOString()})
      })
    ))
  }

  public syncUpdateGroupRenderData(renderData: Partial<FirebaseRenderData>) {
    return firstValueFrom(this.groupId$.pipe(
      filter(groupId => !!groupId),
      take(1),
      withLatestFrom(this.store.select(selectRenderId)),
      switchMap(async ([groupId, renderId]) => {
        const docRef = doc(this.firestore, `groups/${groupId}/renders/${renderId}`)
        return await updateDoc(docRef, {...renderData, lastUpdated: new Date().toISOString()})
      })
    ))
  }

  public updateRenderData(renderData: Partial<FirebaseRenderData>) {
    return firstValueFrom(this.userId$.pipe(
      filter(userId => !!userId),
      take(1),
      switchMap(async userId => {
        const docRef = doc(this.firestore, `users/${userId}/renders/${renderData.id}`)
        return await updateDoc(docRef, {...renderData, lastUpdated: new Date().toISOString()})
      })
    ))
  }

  public overwriteRenderData(renderData: FirebaseRenderData) {
    return firstValueFrom(this.userId$.pipe(
      filter(userId => !!userId),
      take(1),
      switchMap(async userId => {
        const docRef = doc(this.firestore, `users/${userId}/renders/${renderData.id}`)
        return await setDoc(docRef, renderData)
      })
    ))
  }

  public overwriteGroupRenderData(renderData: GroupRenderData, groupId: string) {
    return firstValueFrom(this.userId$.pipe(
      filter(userId => !!userId),
      take(1),
      switchMap(async userId => {
        const docRef = doc(this.firestore, `groups/${groupId}/renders/${renderData.id}`)
        return await setDoc(docRef, renderData)
      })
    ))
  }

  public async getCurrentUsageStats(): Promise<FirebaseUsageStats> {
    return (await getDoc(doc(this.firestore, 'activeStats/stats'))).data() as FirebaseUsageStats
  }

  public getGroupRenderData(groupId: string,renderId: string): Observable<GroupRenderData | undefined>{
    return this.documentSnapshot$<GroupRenderData>(doc(this.firestore, `groups/${groupId}/renders/${renderId}`))
  }

  public async deleteGroupFolder(folderId: string) {
    return firstValueFrom(this.groupId$.pipe(
      filter(groupId => !!groupId),
      take(1),
      switchMap(async groupId => {
        deleteDoc(doc(this.firestore, `groups/${groupId}/folders/${folderId}`))
      })
    ))
  }

  public async getRenderData(userId: string,renderId: string): Promise<FirebaseRenderData> {
    return (await getDoc(doc(this.firestore, `users/${userId}/renders/${renderId}`))).data() as FirebaseRenderData
  }
}
